<template>
  <div
    id="map-container"
    class="relative"
    :class="{
      'show-map': showMap
    }"
  >
    <GMapMap
      ref="map"
      class="g-map"
      :api-key="gMapsKey"
      map-type-id="terrain"
      gesture-handling="greedy"
      :no-clear="true"
      :zoom-control="false"
      :fullscreen-control="false"
      :map-type-control="false"
      :street-view-control="false"
      :clickable-icons="false"
      @mouseenter="hoveredItemModel = null"
    >
      <template v-if="customPolygon">
        <GMapPolygon
          v-for="(polygon, index) in customPolygon"
          :key="`${index}-polygon`"
          :options="{
            paths: polygon,
            strokeColor: '#e2007c',
            strokeOpacity: 0.75,
            strokeWeight: 2,
            fillColor: '#e2007c',
            fillOpacity: 0.0,
            clickable: false
          }"
        />
      </template>
      <GMapCustomMarker
        v-for="(marker, i) in markers?.filter(Boolean)"
        :key="`${marker.id}-marker`"
        :ref="getRef(`${marker.id}-marker`)"
        :marker="marker.position"
        class="hover:!z-[51]"
        :class="[
          (clickedPin === marker.id || hoveredItem === marker.id) &&
          !polygonDrawIsEnabled
            ? '!z-[51]'
            : '!z-[unset]'
        ]"
      >
        <VDropdown
          :ref="getRef(`${marker.id}-card`)"
          :popper-class="{
            'card-dropdown': true,
            'max-md:hidden': !showMap
          }"
          placement="top"
          strategy="fixed"
          :triggers="[]"
          :shown="
            (clickedPin === marker.id ||
              (hoveredItem === marker.id && !clickedPin)) &&
            !polygonDrawIsEnabled
          "
          :arrow-padding="32"
          :auto-hide="false"
        >
          <div
            class="flex items-center justify-center bg-blue-dark border-white border text-white font-bold min-w-8 px-2 min-h-4 py-0.5 rounded-full text-normal hover:bg-primary hover:border-white hover:text-white hover:!z-[51] transition-colors duration-200"
            :class="{
              '!bg-primary !text-white !z-[51]':
                (clickedPin === marker.id || hoveredItem === marker.id) &&
                !polygonDrawIsEnabled,
              'cursor-pointer': !polygonDrawIsEnabled
            }"
            @click="
              () => {
                if (clickedPin === marker.id) {
                  clickedPinModel = null;
                } else {
                  clickedPinModel = marker.id;
                }
              }
            "
          >
            {{ marker.shortPrice }}
          </div>
          <template #popper>
            <div
              class="relative"
              v-if="
                (clickedPin === marker.id ||
                  (hoveredItem === marker.id && !clickedPin)) &&
                (!getCardData || getCardData(marker.id))
              "
            >
              <component
                :is="marker.projectStatus ? SiteProjectCard : SitePropertyCard"
                :favorite="favorites[marker.id]"
                v-bind="getCardData ? getCardData(marker.id) : marker"
                show-favorite="left"
                hide-carousel
                mini-card
                :data-name="`${i}-card`"
                @click="
                  () => {
                    clickedPinModel = null;
                  }
                "
                @update:favorite="setPropertyAsFavorite(marker, $event)"
              ></component>
              <div
                class="absolute top-0 right-0 min-w-[100px] min-h-[100px] pointer-events-none close-background"
              >
                <button
                  type="button"
                  class="absolute top-0 right-0 p-2 pointer-events-auto"
                  @click="
                    () => {
                      clickedPinModel = null;
                    }
                  "
                >
                  <Icon :path="mdiClose" class="text-neutral/80 w-6" />
                </button>
              </div>
            </div>
            <div
              v-else-if="
                clickedPin === marker.id ||
                (hoveredItem === marker.id && !clickedPin)
              "
              class="flex items-center justify-center p-2 h-[100px] w-[100px] bg-white rounded-md shadow-md !z-[51]"
            >
              <Icon
                :path="mdiLoading"
                class="text-neutral-light h-5 w-5 mx-auto animate-spin"
              />
            </div>
          </template>
        </VDropdown>
      </GMapCustomMarker>
      <GMapCustomMarker
        ref="locationSearchMarker"
        :marker="{
          lat: locationPin?.lat || 0,
          lng: locationPin?.lng || 0
        }"
        class="z-[52]"
      >
        <div
          class="flex w-2 h-2 bg-primary rounded-full"
          :class="{
            hidden: !locationPin
          }"
        >
          <div
            class="w-4 h-4 -m-1 bg-primary/50 rounded-full animate-ping"
          ></div>
        </div>
      </GMapCustomMarker>
    </GMapMap>
    <div
      class="absolute top-3 left-3 right-3 flex flex-wrap justify-center items-center gap-2 z-[55] pointer-events-none"
    >
      <div class="h-8 lg:h-10 flex-1"></div>
      <AppButton
        v-if="polygonControls && customPolygon"
        class="h-8 lg:h-10 px-2 text-sm lg:text-base rounded whitespace-nowrap pointer-events-auto"
        @click="clearPolygon"
      >
        {{ $t('search.removeBoundary') }}
      </AppButton>
      <AppButton
        v-if="polygonControls && !polygonDrawIsEnabled"
        class="h-8 lg:h-10 px-2 text-sm lg:text-base rounded whitespace-nowrap pointer-events-auto"
        @click="enableFreeHand"
      >
        {{ $t('search.addBoundary') }}
      </AppButton>
      <AppButton
        v-if="polygonControls && polygonDrawIsEnabled"
        class="h-8 lg:h-10 px-2 text-sm lg:text-base rounded whitespace-nowrap pointer-events-auto"
        @click="disableFreeHand"
      >
        {{ $t('search.cancelBoundaryDraw') }}
      </AppButton>
      <div class="h-8 lg:h-10 flex-1 flex gap-2">
        <AppButton
          class="ml-auto h-8 lg:h-10 px-2 text-sm lg:text-base rounded whitespace-nowrap pointer-events-auto"
          @click="showSatellite = !showSatellite"
        >
          {{
            showSatellite ? $t('search.viewMap') : $t('search.viewSatellite')
          }}
        </AppButton>
        <AppButton
          class="w-8 lg:w-10 h-8 lg:h-10 pointer-events-auto rounded"
          @click="toggleFullscreen"
        >
          <Icon :path="mdiFullscreen" />
        </AppButton>
      </div>
    </div>
    <div
      class="absolute absolute-center-y right-3 z-[55] pointer-events-none flex flex-col gap-2"
    >
      <AppButton
        class="w-8 lg:w-10 h-8 lg:h-10 pointer-events-auto rounded"
        @click="zoomIn"
      >
        <Icon :path="mdiPlus" />
      </AppButton>
      <AppButton
        class="w-8 lg:w-10 h-8 lg:h-10 pointer-events-auto rounded"
        @click="zoomOut"
      >
        <Icon :path="mdiMinus" />
      </AppButton>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="CardProps extends PropertyCardProps & ProjectCardProps = PropertyCardProps & ProjectCardProps"
>
import {
  mdiClose,
  mdiFullscreen,
  mdiLoading,
  mdiMinus,
  mdiPlus
} from '@mdi/js';
import { Dropdown as VDropdown } from 'floating-vue';
import GMapCustomMarker from 'vue3-gmap-custom-marker';
import { GoogleMap as GMapMap, Polygon as GMapPolygon } from 'vue3-google-map';

import { useSearchStore } from '@/store/search';
import type { ProjectCardProps, PropertyCardProps } from '@/types/properties';
import { processPolygon } from '@/utils/maps';

interface Bounds {
  left: number | null;
  top: number | null;
  right: number | null;
  bottom: number | null;
}

const MAX_ZOOM = 14;
const DEFAULT_ZOOM = 9;

const SitePropertyCardPlaceholder = defineAsyncComponent(
  () => import('@/async-components/Site/PropertyCardPlaceholder.vue')
);
const SiteProjectCard = defineAsyncComponent({
  loader: () => import('@/async-components/Site/ProjectCard.vue'),
  loadingComponent: SitePropertyCardPlaceholder,
  delay: 1
});
const SitePropertyCard = defineAsyncComponent({
  loader: () => import('@/async-components/Site/PropertyCard.vue'),
  loadingComponent: SitePropertyCardPlaceholder,
  delay: 1
});

interface MarkerData {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
  shortPrice: string;
  projectStatus: string;
}

const props = defineProps<{
  markers?: MarkerData[] | CardProps[];
  showMap: boolean;
  favorites: Record<string, boolean>;
  clickedPin: string | null;
  hoveredItem: string | null;
  followHoveredItem?: boolean;
  bounds: Bounds;
  polygonControls?: boolean;
  customPolygon?: google.maps.LatLng[][];
  usingPolygon: boolean;
  getCardData?: (id: string) => CardProps | null;
  locationPin?: { lat: number; lng: number } | null;
}>();

const clickedPinModel = defineModel<string | null>('clickedPin', {
  default: null
});

const hoveredItemModel = defineModel<string | null>('hoveredItem', {
  default: null
});

const boundsModel = defineModel<Bounds | null>('bounds', {
  default: null
});

const usingPolygonModel = defineModel<boolean>('usingPolygon', {
  default: false
});

type Emits = {
  (e: 'update:favorite', property: CardProps, value: boolean): void;
  (e: 'clearPolygon'): void;
  (e: 'pushCustomPolygon', paths: google.maps.LatLng[][]): void;
  (e: 'boundsUpdated'): void;
};

const emit = defineEmits<Emits>();

const {
  public: { gMapsKey }
} = useRuntimeConfig();

const { getRef } = useGetRef();

const searchStore = useSearchStore();

const map = ref<{ map: google.maps.Map }>(null);

const markersFirstLoaded = ref(false);

type Marker = {
  onMap: () => void;
};

const locationSearchMarker = ref<Marker>(null);

function updateLocationSearchPin() {
  updateMarker(locationSearchMarker.value);
}

function updatePins() {
  if (props.markers?.length) {
    props.markers.forEach(marker => {
      if (!marker?.id) {
        return;
      }
      const el = getRef(`${marker.id}-marker`);
      updateMarker(el.value);
    });
  }
}

const polygonDrawIsEnabled = ref(false);

const showSatellite = ref(false);

function zoomIn() {
  const currentMap = map.value?.map;
  if (currentMap) {
    currentMap.setZoom(currentMap.getZoom() + 1);
  }
}

function zoomOut() {
  const currentMap = map.value?.map;
  if (currentMap) {
    currentMap.setZoom(currentMap.getZoom() - 1);
  }
}

function toggleFullscreen() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (document.fullscreenElement || window.compatibleFullscreenElement) {
    window.compatibleFullscreenElement = null;

    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  } else {
    const element = document.getElementById('map-container');
    window.compatibleFullscreenElement = element;

    if (element?.requestFullscreen) {
      element.requestFullscreen();
    } else if (element?.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element?.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element?.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  }
}

watch(
  () => showSatellite.value,
  showSatellite => {
    const currentMap = map.value?.map;
    if (currentMap) {
      currentMap.setMapTypeId(showSatellite ? 'satellite' : 'terrain');
    }
  }
);

function setPropertyAsFavorite(property: CardProps, value: boolean) {
  emit('update:favorite', property, value);
}

function enableFreeHand(e: MouseEvent) {
  e && e.preventDefault();

  const google = {
    maps: map.value.api as google.maps.CoreLibrary &
      google.maps.MapsLibrary & {
        drawing: google.maps.DrawingLibrary;
      }
  };

  const currentMap = map.value?.map as google.maps.Map;

  if (!google.maps || !currentMap) return;

  polygonDrawIsEnabled.value = true;

  currentMap.getDiv().addEventListener('mousedown', drawFreeHand);
  currentMap.getDiv().addEventListener('touchstart', drawFreeHand);
  currentMap.setOptions({
    draggable: false,
    zoomControl: false,
    scrollwheel: false,
    disableDoubleClickZoom: false
  });
  currentMap.setClickableIcons(false);
}

function disableFreeHand() {
  const currentMap = map.value?.map as google.maps.Map;

  if (!currentMap) return;

  polygonDrawIsEnabled.value = false;

  currentMap.getDiv().removeEventListener('mousedown', drawFreeHand);
  currentMap.setOptions({
    draggable: true,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true
  });
  currentMap.setClickableIcons(true);
}

function clearPolygon() {
  emit('clearPolygon');
}

// let notFoundMarkerLogIsDone = false;
// let markerLogIsDone = false;

function updateMarker(marker: Marker | Marker[]) {
  // if (!marker && !notFoundMarkerLogIsDone) {
  //   notFoundMarkerLogIsDone = true;
  //   console.log('[DEBUG] Updating not found marker on map', marker);
  // } else if (marker && !markerLogIsDone) {
  //   markerLogIsDone = true;
  //   console.log('[DEBUG] Updating marker on map', marker);
  // }
  if (Array.isArray(marker)) {
    marker.forEach(m => m?.onMap());
  } else {
    marker?.onMap();
  }
}

function drawFreeHand(_event: MouseEvent) {
  const google = {
    maps: map.value.api as google.maps.CoreLibrary &
      google.maps.MapsLibrary & {
        drawing: google.maps.DrawingLibrary;
      }
  };

  const currentMap = map.value?.map as google.maps.Map;

  if (!google.maps || !currentMap) return;

  // the polygon
  const poly = new google.maps.Polyline({
    map: currentMap,
    clickable: false,
    strokeColor: '#e2007c',
    strokeOpacity: 0.75,
    strokeWeight: 2,
    fillColor: '#e2007c',
    fillOpacity: 0.0
  });

  const moveFunction = function (e: google.maps.KmlMouseEvent) {
    poly.getPath().push(e.latLng);
  };

  // move-listener
  const mouseMove = google.maps.event.addListener(
    currentMap,
    'mousemove',
    moveFunction
  );

  const touchMove = google.maps.event.addListener(
    currentMap,
    'touchmove',
    moveFunction
  );

  let upEventListener: google.maps.MapsEventListener | null = null;
  let upEventListenerTouch: google.maps.MapsEventListener | null = null;

  const upFunction = function (_e) {
    google.maps.event.removeListener(mouseMove);
    google.maps.event.removeListener(touchMove);
    if (upEventListener) {
      google.maps.event.removeListener(upEventListener);
      upEventListener = null;
    }
    if (upEventListenerTouch) {
      google.maps.event.removeListener(upEventListenerTouch);
      upEventListenerTouch = null;
    }
    const path = poly.getPath().getArray();
    const processedPaths = processPolygon(
      path.map(({ lat, lng }: { lat: () => number; lng: () => number }) => ({
        lat: lat(),
        lng: lng()
      })),
      400
    );
    poly.setMap(null);
    poly.setPath([]);
    pushCustomPolygon(processedPaths);
    usingPolygonModel.value = true;
    disableFreeHand();
  };

  // mouseup-listener
  upEventListener = google.maps.event.addListenerOnce(
    currentMap,
    'mouseup',
    upFunction
  );
  upEventListenerTouch = google.maps.event.addListenerOnce(
    currentMap,
    'touchend',
    upFunction
  );
}

let mapInitialized = false;

function updateMap(fitBounds: boolean = false) {
  return new Promise<null | false | number>((resolve, reject) => {
    try {
      if (!map.value) return resolve(null);

      const api = map.value.api;

      if (!api) return resolve(null);

      const currentMap = map.value?.map;

      console.log('[DEBUG] Updating map', fitBounds);

      initializeMap();

      if (!fitBounds) {
        setTimeout(updatePins, 200);
        setTimeout(updateLocationSearchPin, 300);
        return resolve(false);
      }

      // fit map to markers

      setTimeout(() => {
        if (!props.markers?.length) {
          setTimeout(updatePins, 200);
          setTimeout(updateLocationSearchPin, 300);
          return resolve(0);
        }

        const bounds = new api.LatLngBounds();

        props.markers.forEach(marker => {
          bounds.extend(marker.position);
        });

        currentMap.fitBounds(bounds);

        api.event.addListenerOnce(currentMap, 'bounds_changed', () => {
          // zoom out if zoom > MAX_ZOOM
          if (currentMap.getZoom() > MAX_ZOOM) {
            currentMap.setZoom(MAX_ZOOM);
          }

          updatePins();

          updateLocationSearchPin();

          return resolve(props.markers.length);
        });
      }, 500);
    } catch (error) {
      console.error('[ERROR] updateMap', error);
      return reject(error);
    }
  });
}

interface Point {
  lat: number;
  lng: number;
}

function pushCustomPolygon(paths: Point[][]) {
  emit('pushCustomPolygon', paths);
}

function updateCardDropdown() {
  if (props.markers) {
    const currentCardId = clickedPinModel.value || hoveredItemModel.value;
    if (currentCardId) {
      const dropdownElement = getRef(`${currentCardId}-card`);
      (dropdownElement.value || []).forEach((el: { onResize?: () => void }) => {
        if (el.onResize) {
          el.onResize();
        }
      });
    }
  }
}

let previousBounds = '';

const debouncedMapNavigated = debounce(() => {
  const isInMobile = window.innerWidth < 800;

  if (!props.showMap && isInMobile) {
    return;
  }

  const currentMap = map.value?.map;

  if (currentMap && searchStore.setCenter && searchStore.setZoom) {
    const mapCenter = currentMap.getCenter();
    searchStore.setCenter({
      lat: mapCenter.lat(),
      lng: mapCenter.lng()
    });
    searchStore.setZoom(currentMap.getZoom());
  }

  const mapBounds = currentMap.getBounds();

  if (mapBounds) {
    const ne = mapBounds.getNorthEast();
    const sw = mapBounds.getSouthWest();

    boundsModel.value = {
      left: sw.lng(),
      top: ne.lat(),
      right: ne.lng(),
      bottom: sw.lat()
    };

    if (markersFirstLoaded.value) {
      markersFirstLoaded.value = true;
      if (props.markers) {
        return;
      }
    }

    // check if bounds are outside of custom polygon

    if (props.customPolygon) {
      // get the min and max lat and lng
      const minLat = Math.min(
        ...props.customPolygon.map(polygon =>
          Math.min(...polygon.map(({ lat }) => lat))
        )
      );
      const maxLat = Math.max(
        ...props.customPolygon.map(polygon =>
          Math.max(...polygon.map(({ lat }) => lat))
        )
      );
      const minLng = Math.min(
        ...props.customPolygon.map(polygon =>
          Math.min(...polygon.map(({ lng }) => lng))
        )
      );
      const maxLng = Math.max(
        ...props.customPolygon.map(polygon =>
          Math.max(...polygon.map(({ lng }) => lng))
        )
      );
      if (
        props.bounds.left < minLng ||
        props.bounds.right > maxLng ||
        props.bounds.top > maxLat ||
        props.bounds.bottom < minLat
      ) {
        return;
      }
    }

    const currentBounds = Object.values(props.bounds || {})
      .filter(Boolean)
      .join(',');

    if (!currentBounds || currentBounds === previousBounds) {
      return;
    }

    previousBounds = currentBounds;

    console.log('[DEBUG] Bounds updated', currentBounds);

    emit('boundsUpdated');
  }
}, 10);

function initializeMap() {
  if (mapInitialized) {
    return;
  }

  mapInitialized = true;

  const currentMap = map.value?.map;

  window.map = currentMap;

  if (window.goingBack) {
    console.log(
      'using stored center and zoom',
      searchStore.center,
      searchStore.zoom,
      window.goingBack
    );
    currentMap.setCenter(
      searchStore.center || { lat: 25.761681, lng: -80.191788 }
    );
    currentMap.setZoom(searchStore.zoom || DEFAULT_ZOOM);
    if (searchStore.center && searchStore.zoom) {
      debouncedMapNavigated(false);
      console.log('[DEBUG] Ignoring bounds forced false');
    }
  } else {
    currentMap.setCenter({ lat: 25.761681, lng: -80.191788 });
    currentMap.setZoom(DEFAULT_ZOOM);
  }

  // currentMap.setCenter({ lat: 25.761681, lng: -80.191788 });
  // currentMap.setZoom(DEFAULT_ZOOM);

  google.maps.event.addListener(currentMap, 'bounds_changed', () => {
    updateCardDropdown();
    setTimeout(updateCardDropdown, 100);
  });
  google.maps.event.addListener(currentMap, 'idle', debouncedMapNavigated);
}

let focusItemTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.hoveredItem,
  () => {
    if (focusItemTimeout) {
      clearTimeout(focusItemTimeout);
    }

    if (!props.hoveredItem || !props.followHoveredItem) {
      return;
    }

    const marker = props.markers?.find(m => m.id === props.hoveredItem);

    if (!marker) {
      return;
    }

    const currentMap = map.value?.map;

    if (!currentMap) {
      return;
    }

    const focusItemId = props.hoveredItem;

    focusItemTimeout = setTimeout(() => {
      if (props.hoveredItem !== focusItemId) {
        return;
      }

      currentMap.setCenter(marker.position);

      // TODO: use panTo

      google.maps.event.addListenerOnce(currentMap, 'idle', () => {
        setTimeout(() => {
          updateCardDropdown();
        }, 50);
        // setTimeout(() => {
        //   updateCardDropdown();
        // }, 100);
      });
    }, 100);
  },
  { immediate: true, flush: 'post' }
);

watch(
  () => map.value?.ready,
  ready => {
    if (ready) {
      updateMap();
    }
  },
  { immediate: true, flush: 'post' }
);

defineExpose({
  updateMap
});
</script>

<style scoped lang="scss">
:deep(.gm-bundled-control) {
  display: none;
}
.close-background {
  background: radial-gradient(
    circle at top right,
    rgba(255, 255, 255, 0.5) 12.5%,
    transparent 50%
  );
  border-top-right-radius: 20px;
}
</style>
